import { type ReactNode } from "react";
import {
  DenseTable,
  StatusBadge,
  type Column,
} from "../../components/index.ts";
import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import type { ApiError } from "../../lib/api.ts";
import type { UseQueryResult } from "@tanstack/react-query";
import {
  type McpToolSurfacePage,
  type McpSurfaceTool,
  type McpToolSurfaceDriftPage,
  type McpToolSurfaceDriftEntry,
} from "./use-mcp-gateway.ts";
import { surfaceChangeLabel, driftLevel, driftStatusText } from "./vocab.ts";

/**
 * The Tool-surface tab (task 7.2; requirement 13.2, FR-6). It shows, per upstream behind the gateway
 * MCP:
 *
 * - the current inventory — one row per surfaced tool, each with an enforced badge (and a flag for
 *   default-deny) — from `GET /api/v1/mcp/tool-surface`, including the "tools exposed without
 *   enforcement" total, which an operator must be able to see at a glance; and
 * - the drift timeline — newest first — from `GET /api/v1/mcp/tool-surface/drift`, with critical
 *   drift (a description/schema change on a previously seen tool) highlighted, and unrecorded drift
 *   (inferred from a hash change with no drift event, FR-6.4) surfaced as a warning so it is never
 *   silently swallowed.
 *
 * Both reads are catalog-honest: an unenforced tool is shown with an explicit "Unenforced" badge
 * (grey, never green), and drift is never deduplicated away.
 */
export function ToolSurfaceTab(props: {
  readonly surface: UseQueryResult<McpToolSurfacePage, ApiError>;
  readonly drift: UseQueryResult<McpToolSurfaceDriftPage, ApiError>;
}): ReactNode {
  return (
    <div className="mcp-surface">
      <InventorySection query={props.surface} />
      <DriftSection query={props.drift} />
    </div>
  );
}

function InventorySection(props: {
  query: UseQueryResult<McpToolSurfacePage, ApiError>;
}): ReactNode {
  const { data, isError, error, isPending } = props.query;

  const columns: readonly Column<McpSurfaceTool>[] = [
    { key: "upstream", header: "Upstream", isRowHeader: true, cell: (r) => <code>{r.upstream_id}</code> },
    { key: "tool", header: "Tool", cell: (r) => <code>{r.tool}</code> },
    {
      key: "enforced",
      header: "Enforcement",
      cell: (r) =>
        r.enforced ? (
          <StatusBadge level="healthy" label="Enforced" size="sm" />
        ) : (
          <StatusBadge
            level="warn"
            label="Unenforced"
            size="sm"
            title="This tool is exposed without enforcement."
          />
        ),
    },
    {
      key: "default_deny",
      header: "Default deny",
      cell: (r) =>
        r.default_deny ? (
          <StatusBadge level="healthy" label="Yes" size="sm" />
        ) : (
          <StatusBadge level="unknown" label="No" size="sm" />
        ),
    },
    {
      key: "first_seen",
      header: "First seen",
      align: "right",
      cell: (r) => <time dateTime={isoAttr(r.first_seen)}>{formatDateTime(r.first_seen)}</time>,
    },
    {
      key: "last_seen",
      header: "Last seen",
      align: "right",
      cell: (r) => <time dateTime={isoAttr(r.last_seen)}>{formatDateTime(r.last_seen)}</time>,
    },
  ];

  return (
    <section className="mcp-surface__section" aria-label="Tool-surface inventory">
      <header className="mcp-surface__header">
        <h3 className="mcp-surface__title">Inventory</h3>
        {data !== undefined && (
          <p className="mcp-surface__counts">
            <span>{(data.tools_total ?? 0).toLocaleString()} tools</span>
            <span
              className="mcp-surface__unenforced"
              data-flag={(data.tools_unenforced ?? 0) > 0 ? "warn" : undefined}
            >
              {(data.tools_unenforced ?? 0).toLocaleString()} unenforced
            </span>
          </p>
        )}
      </header>
      {isPending ? (
        <p className="mcp-surface__state" role="status" aria-live="polite">
          Loading the tool-surface inventory…
        </p>
      ) : isError ? (
        <p className="mcp-surface__state" data-kind="error">
          {surfaceErrorMessage(error, "inventory")}
        </p>
      ) : (
        <DenseTable
          caption="Current tool-surface inventory per upstream"
          captionHidden
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(r) => `${r.upstream_id}:${r.tool}`}
          stickyHeader
          emptyMessage="No tools are surfaced for any upstream in this window."
        />
      )}
    </section>
  );
}

function DriftSection(props: {
  query: UseQueryResult<McpToolSurfaceDriftPage, ApiError>;
}): ReactNode {
  const { data, isError, error, isPending } = props.query;

  const columns: readonly Column<McpToolSurfaceDriftEntry>[] = [
    {
      key: "ts",
      header: "When",
      cell: (r) => <time dateTime={isoAttr(r.ts)}>{formatDateTime(r.ts)}</time>,
    },
    { key: "upstream", header: "Upstream", cell: (r) => <code>{r.upstream_id}</code> },
    { key: "tool", header: "Tool", cell: (r) => <code>{r.tool}</code> },
    { key: "change", header: "Change", cell: (r) => surfaceChangeLabel(r.change) },
    {
      key: "status",
      header: "Severity",
      cell: (r) => (
        <StatusBadge
          level={driftLevel(r)}
          label={driftStatusText(r)}
          size="sm"
          title={
            r.critical
              ? "A description or schema change on a previously seen tool."
              : !r.recorded
                ? "Inferred from a hash change with no drift event (unrecorded)."
                : "A recorded surface change."
          }
        />
      ),
    },
  ];

  return (
    <section className="mcp-surface__section" aria-label="Tool-surface drift timeline">
      <h3 className="mcp-surface__title">Drift timeline</h3>
      {isPending ? (
        <p className="mcp-surface__state" role="status" aria-live="polite">
          Loading the drift timeline…
        </p>
      ) : isError ? (
        <p className="mcp-surface__state" data-kind="error">
          {surfaceErrorMessage(error, "drift timeline")}
        </p>
      ) : (
        <DenseTable
          caption="Tool-surface drift, newest first; critical drift highlighted"
          captionHidden
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(r, i) => `${r.upstream_id}:${r.tool}:${r.ts}:${i}`}
          stickyHeader
          emptyMessage="No tool-surface drift was recorded in this window."
        />
      )}
    </section>
  );
}

function surfaceErrorMessage(error: ApiError | null, what: string): string {
  if (error === null) return `The ${what} could not load.`;
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return `You are not permitted to see the ${what}. The server enforces access.`;
    case "network":
      return `The ${what} request could not be sent. Check your connection.`;
    default:
      return error.message;
  }
}
